import {
    exportX25519PrivateKeyBytes,
    exportX25519PublicKey,
    generatePhrase,
    generateSalt,
    generateX25519KeyPair,
    getEd25519PublicKeyFromPrivate,
    importEd25519PrivateKey,
    phraseToSeed,
    serializeWrappedKey,
    wrapMailboxKey,
} from '@sisumail/crypto';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/auth';
import { PhraseStep } from '../components/auth/PhraseStep';
import { VerifyStep } from '../components/auth/VerifyStep';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { logger } from '../lib/logger';

export default function SignUp() {
    const navigate = useNavigate();
    const { completeRegistration } = useAuth();

    // Steps: 'initializing' -> 'phrase' -> 'verify' -> 'registering'
    const [step, setStep] = useState<'initializing' | 'phrase' | 'verify' | 'registering'>(
        'initializing'
    );
    const [username, setUsername] = useState('');
    const [phrase, setPhrase] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const location = useLocation();

    // Check for reservation passed from Landing or Session Storage
    useEffect(() => {
        const stateUsername = location.state?.username;
        // Check session storage as fallback
        const sessionUsername = sessionStorage.getItem('reserved_handle');

        // We don't strictly need expires_at for the UI flow,
        // the API will reject if it's expired anyway.

        const targetUsername = stateUsername || sessionUsername;

        if (targetUsername) {
            setUsername(targetUsername);
            logger.info('SignUp: Resumed from Landing/Session', {
                username: targetUsername,
            });

            // Auto-advance to phrase
            const newPhrase = generatePhrase();
            setPhrase(newPhrase);
            setStep('phrase');
        } else {
            // No reservation found, redirect to Landing
            logger.warn('SignUp: No reservation found, redirecting to Landing');
            navigate('/', { replace: true });
        }
    }, [location.state, navigate]);

    const handlePhraseVerified = async () => {
        setStep('registering');
        setLoading(true);
        try {
            const recoverySalt = generateSalt();
            const seed = await phraseToSeed(phrase);
            const authPrivateKey = await importEd25519PrivateKey(seed);
            const authPublicKeyB64 = await getEd25519PublicKeyFromPrivate(authPrivateKey);
            const mailKeyPair = await generateX25519KeyPair();
            const mailPublicKeyB64 = await exportX25519PublicKey(mailKeyPair.publicKey);

            // Get raw bytes directly
            const mailPrivateKeyBytes = await exportX25519PrivateKeyBytes(mailKeyPair.privateKey);
            const wrapped = await wrapMailboxKey(mailPrivateKeyBytes, phrase);
            const kmRecoveryWrap = serializeWrappedKey(wrapped);

            const { data: res } = await api.auth.register({
                identity_id: username,
                km_recovery_wrap: kmRecoveryWrap,
                recovery_salt: recoverySalt,
                recovery_kdf_params: 'PBKDF2-SHA256-200k',
                recovery_public_key: authPublicKeyB64,
                mail_public_key: mailPublicKeyB64,
            });

            if (!res.success) throw new Error('Registration failed');

            await completeRegistration(username, authPrivateKey);
            navigate('/account', { state: { username } });
        } catch (err: any) {
            setError(err.message || 'Registration failed');
            setStep('verify');
        } finally {
            setLoading(false);
        }
    };

    if (step === 'initializing') {
        return (
            <AuthLayout title="Initializing" subtitle="Setting up secure environment...">
                <div className="flex justify-center py-12">
                    <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
                </div>
            </AuthLayout>
        );
    }

    if (step === 'phrase') {
        return (
            <PhraseStep
                username={username}
                phrase={phrase}
                onNext={() => setStep('verify')}
                // No back button, as we don't want them going back to an empty form
            />
        );
    }

    if (step === 'verify') {
        return (
            <VerifyStep
                phrase={phrase}
                status={loading ? 'registering' : 'idle'}
                onVerify={handlePhraseVerified}
                onBack={() => setStep('phrase')}
                error={error}
            />
        );
    }

    return (
        <AuthLayout title="Registering" subtitle="Finalizing your secure identity...">
            <div className="flex justify-center py-12">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
            </div>
        </AuthLayout>
    );
}
